import { getSeedMindmap } from './MindmapState';
import type { Mindmap, MindmapMeta } from './MindmapTypes';
import { exportMindmapToText, importMindmapFromText } from './MindmapText';
import { TEXT_STORAGE_KEY } from './MindmapTextMigration';

/**
 * Multi-map persistence layer
 * - Index: mindmap-v2:active -> just the activeId string
 * - Per-map snapshot: mindmap-v2:text:<id> (same text format used by TEXT_STORAGE_KEY)
 * - List of mindmaps discovered by scanning localStorage for mindmap-v2:text:* keys
 */

const ACTIVE_KEY = 'mindmap-v2:active';

interface StoredMindmapMeta extends MindmapMeta {
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface MindmapIndexItem {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface MindmapIndex {
  activeId: string | null;
  items: MindmapIndexItem[];
}

const nowIso = () => new Date().toISOString();

const genId = () => {
  try {
    // @ts-ignore
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return 'm-' + Math.random().toString(36).slice(2, 10);
};

function mapKey(id: string) {
  return `mindmap-v2:text:${id}`;
}

function readActiveId(storage: Storage): string | null {
  try {
    return storage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

function writeActiveId(storage: Storage, id: string | null): void {
  try {
    if (id) {
      storage.setItem(ACTIVE_KEY, id);
    } else {
      storage.removeItem(ACTIVE_KEY);
    }
  } catch {}
}

function scanMindmapIds(storage: Storage): string[] {
  const ids: string[] = [];
  const prefix = 'mindmap-v2:text:';
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix)) {
        const id = key.substring(prefix.length);
        if (id) ids.push(id);
      }
    }
  } catch {}
  return ids;
}

const MIRROR_TEXT_SHOWCASE = false; // keep a single per-map key; no showcase mirror

function ensureActiveMirrorsTextKey(storage: Storage, mindmap: Mindmap): void {
  if (!MIRROR_TEXT_SHOWCASE) return;
  try {
    const text = exportMindmapToText(mindmap);
    storage.setItem(TEXT_STORAGE_KEY, text);
  } catch {}
}

function saveMapSnapshot(storage: Storage, id: string, mindmap: Mindmap): void {
  const text = exportMindmapToText(mindmap);
  storage.setItem(mapKey(id), text);
}

function loadMapSnapshot(storage: Storage, id: string): Mindmap | null {
  const raw = storage.getItem(mapKey(id));
  if (!raw) return null;
  try {
    const m = importMindmapFromText(raw);
    return { ...m, id };
  } catch {
    return null;
  }
}

function normalizeMeta(meta?: MindmapMeta | null): StoredMindmapMeta {
  const base: MindmapMeta = meta ? { ...meta } : {};
  const title = typeof base.title === 'string' && base.title.trim() ? base.title.trim() : 'Mindmap';
  const createdAtCandidate = typeof base.createdAt === 'string' && base.createdAt ? base.createdAt : undefined;
  const updatedAtCandidate = typeof base.updatedAt === 'string' && base.updatedAt ? base.updatedAt : undefined;
  const createdAt = createdAtCandidate ?? nowIso();
  const updatedAt = updatedAtCandidate ?? createdAt;
  return {
    ...base,
    title,
    createdAt,
    updatedAt,
  };
}

function metadataMatches(normalized: StoredMindmapMeta, raw: MindmapMeta): boolean {
  const rawTitle = typeof raw.title === 'string' && raw.title.trim() ? raw.title.trim() : 'Mindmap';
  return (
    rawTitle === normalized.title &&
    raw.createdAt === normalized.createdAt &&
    raw.updatedAt === normalized.updatedAt &&
    raw.importedAt === normalized.importedAt &&
    raw.directionPreference === normalized.directionPreference
  );
}

interface MindmapViewRecord {
  viewport?: MindmapViewport;
  meta?: StoredMindmapMeta;
  collapsed?: string[];
}

function parseViewport(value: any): MindmapViewport | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const x = value.x;
  const y = value.y;
  const zoom = value.zoom;
  if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(zoom)) {
    return { x, y, zoom };
  }
  return undefined;
}

function readViewRecord(storage: Storage, id: string): MindmapViewRecord | null {
  try {
    const raw = storage.getItem(viewportKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const record: MindmapViewRecord = {};

    const viewport = parseViewport((parsed as any).viewport ?? parsed);
    if (viewport) record.viewport = viewport;

    if ((parsed as any).meta && typeof (parsed as any).meta === 'object') {
      const normalized = normalizeMeta((parsed as any).meta as MindmapMeta);
      record.meta = normalized;
      if (!metadataMatches(normalized, (parsed as any).meta as MindmapMeta)) {
        writeViewRecord(storage, id, { ...record });
        return { ...record };
      }
    }

    // Collapsed ids (optional)
    if (Array.isArray((parsed as any).collapsed)) {
      const arr = (parsed as any).collapsed.filter((v: any) => typeof v === 'string' && v.trim().length > 0);
      if (arr.length) record.collapsed = arr as string[];
    }

    return record;
  } catch {
    return null;
  }
}

function writeViewRecord(storage: Storage, id: string, record: MindmapViewRecord): void {
  const payload: any = {};
  if (record.viewport) payload.viewport = record.viewport;
  if (record.meta) payload.meta = record.meta;
  if (record.collapsed) payload.collapsed = record.collapsed;
  const hasData = Object.keys(payload).length > 0;
  try {
    if (!hasData) storage.removeItem(viewportKey(id));
    else storage.setItem(viewportKey(id), JSON.stringify(payload));
  } catch {}
}

function readMindmapMeta(storage: Storage, id: string): StoredMindmapMeta | null {
  const record = readViewRecord(storage, id);
  return record?.meta ?? null;
}

function writeMindmapMeta(storage: Storage, id: string, meta: MindmapMeta | StoredMindmapMeta): StoredMindmapMeta {
  const normalized = normalizeMeta(meta);
  const record = readViewRecord(storage, id) ?? {};
  writeViewRecord(storage, id, { ...record, meta: normalized });
  return normalized;
}

function ensureMindmapMeta(storage: Storage, id: string, fallback?: MindmapMeta): StoredMindmapMeta {
  const existing = readMindmapMeta(storage, id);
  if (existing) return existing;
  const metaToPersist: MindmapMeta = fallback ? { ...fallback } : {};
  return writeMindmapMeta(storage, id, metaToPersist);
}

function loadMindmapWithMeta(storage: Storage, id: string): Mindmap | null {
  const map = loadMapSnapshot(storage, id);
  if (!map) return null;
  const meta = ensureMindmapMeta(storage, id, map.meta);
  // Overlay view-level collapsed states (if any) to restore exact view
  const view = readViewRecord(storage, id);
  let nodes = map.nodes;
  if (view?.collapsed && Array.isArray(view.collapsed)) {
    const set = new Set(view.collapsed);
    nodes = map.nodes.map((n) => (set.has(n.id) ? { ...n, collapsed: true } : { ...n, collapsed: undefined }));
  }
  return { ...map, nodes, meta };
}

function buildIndexItem(storage: Storage, id: string): MindmapIndexItem | null {
  const map = loadMindmapWithMeta(storage, id);
  if (!map || !map.meta) return null;
  const normalized = normalizeMeta(map.meta);
  return { id, title: normalized.title, createdAt: normalized.createdAt, updatedAt: normalized.updatedAt };
}

export interface MindmapViewport {
  x: number;
  y: number;
  zoom: number;
}

function viewportKey(id: string) {
  return `mindmap-v2:view:${id}`;
}

export function loadMindmapViewport(id: string, storage: Storage = window.localStorage): MindmapViewport | null {
  const record = readViewRecord(storage, id);
  return record?.viewport ?? null;
}

export function saveMindmapViewport(id: string, viewport: MindmapViewport, storage: Storage = window.localStorage): void {
  if (!Number.isFinite(viewport.x) || !Number.isFinite(viewport.y) || !Number.isFinite(viewport.zoom)) return;
  const record = readViewRecord(storage, id) ?? {};
  writeViewRecord(storage, id, { ...record, viewport });
}

// Collapsed view state -------------------------------------------------------

export function loadMindmapCollapsed(id: string, storage: Storage = window.localStorage): string[] | null {
  const record = readViewRecord(storage, id);
  return record?.collapsed ?? null;
}

export function saveMindmapCollapsed(id: string, collapsedIds: string[], storage: Storage = window.localStorage): void {
  const record = readViewRecord(storage, id) ?? {};
  const unique = Array.from(new Set(collapsedIds.filter((v) => typeof v === 'string' && v.trim().length > 0)));
  writeViewRecord(storage, id, { ...record, collapsed: unique });
}

/**
 * Ensure active mindmap exists. If not, migrate from TEXT_STORAGE_KEY or create seed.
 * Returns the active mindmap (may be newly created).
 */
export function ensureIndex(storage: Storage = window.localStorage): { index: MindmapIndex; migratedActive?: Mindmap } {
  // Scan for existing mindmaps
  const existingIds = scanMindmapIds(storage);
  const activeId = readActiveId(storage);
  const indexedItems = existingIds
    .map((id) => buildIndexItem(storage, id))
    .filter((item): item is MindmapIndexItem => Boolean(item));

  // If we have an active ID and it exists, we're done
  if (activeId && existingIds.includes(activeId)) {
    return { index: { activeId, items: indexedItems } };
  }

  // If we have mindmaps but no/invalid activeId, use first one
  if (existingIds.length > 0) {
    const firstId = existingIds[0];
    writeActiveId(storage, firstId);
    return { index: { activeId: firstId, items: indexedItems } };
  }

  // No mindmaps exist - migrate from legacy or create seed
  let active: Mindmap | null = null;
  const legacyText = storage.getItem(TEXT_STORAGE_KEY);
  if (legacyText) {
    try {
      active = importMindmapFromText(legacyText);
    } catch {
      active = null;
    }
  }
  if (!active) {
    active = getSeedMindmap();
    const t = nowIso();
    active = { ...active, meta: { ...(active.meta ?? {}), createdAt: t, updatedAt: t } };
  }

  const id = genId();
  // CRITICAL: Update the mindmap's id property to match the generated UUID
  active = { ...active, id };

  // Save the new mindmap and set as active
  const initializedMeta = writeMindmapMeta(storage, id, (active.meta ?? {}) as MindmapMeta);
  active = { ...active, meta: initializedMeta };
  saveMapSnapshot(storage, id, active);
  writeActiveId(storage, id);
  ensureActiveMirrorsTextKey(storage, active);

  const items = [{
    id,
    title: initializedMeta.title,
    createdAt: initializedMeta.createdAt,
    updatedAt: initializedMeta.updatedAt,
  }];

  return { index: { activeId: id, items }, migratedActive: active };
}

export function listMindmaps(storage: Storage = window.localStorage): MindmapIndex {
  // Scan localStorage for all mindmap-v2:text:* keys
  const ids = scanMindmapIds(storage);
  const items = ids
    .map((id) => buildIndexItem(storage, id))
    .filter((item): item is MindmapIndexItem => Boolean(item));

  items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  return { activeId: readActiveId(storage), items };
}

export function getActiveMindmapId(storage: Storage = window.localStorage): string | null {
  const { index } = ensureIndex(storage);
  return index.activeId;
}

export function setActiveMindmapId(id: string, storage: Storage = window.localStorage): Mindmap | null {
  // Check if mindmap exists
  const existingIds = scanMindmapIds(storage);
  if (!existingIds.includes(id)) return null;
  
  const map = loadMindmapWithMeta(storage, id);
  if (!map) return null;
  
  // Set as active
  writeActiveId(storage, id);
  ensureActiveMirrorsTextKey(storage, map);
  return map;
}

export function loadActiveMindmap(storage: Storage = window.localStorage): Mindmap | null {
  const { index } = ensureIndex(storage);
  const id = index.activeId;
  if (!id) return null;
  
  const map = loadMindmapWithMeta(storage, id);
  if (!map) return null;
  
  ensureActiveMirrorsTextKey(storage, map);
  return map;
}

export function createNewMindmap(title = 'Untitled', storage: Storage = window.localStorage): Mindmap {
  const id = genId();
  const seed = getSeedMindmap();
  const t = nowIso();
  const baseMeta: MindmapMeta = { ...(seed.meta ?? {}), title, createdAt: t, updatedAt: t };
  const meta = writeMindmapMeta(storage, id, baseMeta);
  const map: Mindmap = { ...seed, id, meta };
  
  // Save and set as active
  saveMapSnapshot(storage, id, map);
  writeActiveId(storage, id);
  ensureActiveMirrorsTextKey(storage, map);
  return map;
}

export function renameMindmap(id: string, newTitle: string, storage: Storage = window.localStorage): Mindmap | null {
  const map = loadMindmapWithMeta(storage, id);
  if (!map) return null;
  
  const t = nowIso();
  const existingMeta = map.meta ?? ensureMindmapMeta(storage, id);
  const title = newTitle.trim() || existingMeta.title;

  const meta = writeMindmapMeta(storage, id, { ...existingMeta, ...(map.meta ?? {}), title, updatedAt: t });
  const next: Mindmap = { ...map, meta };

  saveMapSnapshot(storage, id, next);
  const activeId = readActiveId(storage);
  if (activeId === id) ensureActiveMirrorsTextKey(storage, next);
  return next;
}

export function saveActiveMindmap(mindmap: Mindmap, storage: Storage = window.localStorage): void {
  const id = mindmap.id;
  const now = nowIso();
  const existingMeta = readMindmapMeta(storage, id) ?? mindmap.meta;
  const baseMeta: MindmapMeta = {
    ...(existingMeta ?? {}),
    ...(mindmap.meta ?? {}),
  };
  if (!baseMeta.createdAt) baseMeta.createdAt = existingMeta?.createdAt ?? mindmap.meta?.createdAt ?? now;
  baseMeta.updatedAt = now;

  const meta = writeMindmapMeta(storage, id, baseMeta);
  const withMeta: Mindmap = { ...mindmap, id, meta };

  // Set as active (this is the mindmap being worked on)
  writeActiveId(storage, id);

  // Persist snapshot under its own id
  saveMapSnapshot(storage, id, withMeta);

  // Mirror text snapshot for active map
  ensureActiveMirrorsTextKey(storage, withMeta);
}

export function deleteMindmap(id: string, storage: Storage = window.localStorage): Mindmap | null {
  // Check if mindmap exists
  const existingIds = scanMindmapIds(storage);
  if (!existingIds.includes(id)) return null;
  
  const activeId = readActiveId(storage);
  const wasActive = activeId === id;
  
  // Remove snapshot and viewport
  try {
    storage.removeItem(mapKey(id));
    storage.removeItem(viewportKey(id));
  } catch {}

  // Check remaining mindmaps after deletion
  const remainingIds = scanMindmapIds(storage);
  
  if (remainingIds.length === 0) {
    // Re-seed with a fresh map if none remain
    const newId = genId();
    const seed = getSeedMindmap();
    const t = nowIso();
    const title = seed.meta?.title?.trim() || 'Mindmap';
    const baseMeta: MindmapMeta = { ...(seed.meta ?? {}), title, createdAt: t, updatedAt: t };
    const meta = writeMindmapMeta(storage, newId, baseMeta);
    const map: Mindmap = { ...seed, id: newId, meta };
    
    saveMapSnapshot(storage, newId, map);
    writeActiveId(storage, newId);
    ensureActiveMirrorsTextKey(storage, map);
    return map;
  }

  // If we deleted the active one, switch to the first available
  if (wasActive) {
    const nextId = remainingIds[0];
    writeActiveId(storage, nextId);
    const map = loadMindmapWithMeta(storage, nextId);
    if (map) {
      ensureActiveMirrorsTextKey(storage, map);
      return map;
    }
  }
  
  // Return current active (unchanged if we didn't delete the active one)
  const currentActiveId = readActiveId(storage);
  if (currentActiveId) {
    const map = loadMindmapWithMeta(storage, currentActiveId);
    if (map) {
      ensureActiveMirrorsTextKey(storage, map);
      return map;
    }
  }
  
  return null;
}

function nextDuplicateTitle(base: string, existingTitles: string[]): string {
  const re = new RegExp(`^${base.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}(?: \\((\\d+)\\))?$`);
  let max = 1;
  for (const title of existingTitles) {
    const m = title.match(re);
    if (!m) continue;
    const n = m[1] ? parseInt(m[1], 10) : 1;
    if (n >= max) max = n;
  }
  const next = max + 1;
  return max === 1 ? `${base} (2)` : `${base} (${next})`;
}

export function duplicateMindmap(id: string, storage: Storage = window.localStorage): Mindmap | null {
  const src = loadMindmapWithMeta(storage, id);
  if (!src) return null;
  
  // Get all existing titles to generate unique duplicate title
  const existingIds = scanMindmapIds(storage);
  const existingTitles: string[] = [];
  for (const existingId of existingIds) {
    const meta = readMindmapMeta(storage, existingId) ?? loadMindmapWithMeta(storage, existingId)?.meta;
    if (meta?.title) existingTitles.push(meta.title);
  }
  
  const baseTitle = src.meta?.title || 'Untitled';
  const newTitle = nextDuplicateTitle(baseTitle, existingTitles.filter(t => t.startsWith(baseTitle)));
  const newId = genId();
  const t = nowIso();
  const baseMeta: MindmapMeta = { ...(src.meta ?? {}), title: newTitle, createdAt: t, updatedAt: t };
  const meta = writeMindmapMeta(storage, newId, baseMeta);
  const dup: Mindmap = {
    ...src,
    id: newId,
    meta,
  };
  
  // Save and set as active
  saveMapSnapshot(storage, newId, dup);
  writeActiveId(storage, newId);
  ensureActiveMirrorsTextKey(storage, dup);
  return dup;
}
